import React, {ChangeEvent, useEffect, useState} from "react";
import DataGrid, {
    CellClickArgs,
    Column,
    CopyEvent,
    HeaderRendererProps,
    SelectColumn,
    SortColumn,
    SortDirection
} from "react-data-grid";
import styles from "./index.module.css";
import 'react-data-grid/lib/styles.css';
import './react-data-grid.css';
import {Utils} from "../../helpers/Utils";
import {Checkbox, DropdownItemProps, Form, Icon} from 'semantic-ui-react';

export enum ColumnFormat {
    none,
    date,
    datetime
}

export interface FilterableOptions {
    dropdownItems?: DropdownItemProps[];
}

export interface ColumnDefinition<TItem> {
    key: string,
    name: string,
    accessor?: (item: TItem) => any,
    cellClass?: (item: TItem) => string | undefined,
    resizable?: boolean,
    sortable?: boolean,
    sortDirection?: SortDirection,
    format?: ColumnFormat,
    width?: number | string;
    filterable?: boolean;
    filterableOptions?: FilterableOptions
}

export type DataGridSelection = {
    mode: 'all' | 'ids', selectedIds: string[]
}

interface Props<TItem> {
    columns?: ColumnDefinition<TItem>[];
    keyProperty: string;
    items?: TItem[];
    focusedItem?: TItem;
    onFocusedItemChange?: (item?: TItem) => void;
    onSortColumnsChanged?: (sortColumns: SortColumn[]) => void;
    onItemDoubleClick?: (item: TItem) => void;
    multiSelect?: boolean;
    selectedIds?: string[];
    onFilterChanged?: (value: Map<string, any>) => void;
    height?: string;
    onSelectionChanged?: (selection: DataGridSelection) => void;
}


const AppListDataGrid = function <TItem>(props: Props<TItem>) {
    const [focusedItem, setFocusedItem] = useState<TItem | undefined>(props.focusedItem);
    const [filter, setFilter] = useState<Map<string, any>>(new Map<string, any>());

    function getSortColumns(): SortColumn[] {
        const result: SortColumn[] = [];
        if (props.columns) {
            for (const column of props.columns) {
                if (column.sortDirection) {
                    result.push({columnKey: column.key, direction: column.sortDirection});
                }
            }
        }
        return result;
    }

    const [sortColumns, setSortColumns] = useState<SortColumn[]>(getSortColumns());
    const multiSelect = props.multiSelect ?? false;
    const [selectedRows, setSelectedRows] = useState<Set<string | number>>(new Set(props.selectedIds ?? []));
    const [selectedAll, setSelectedAll] = useState(false);

    useEffect(() => {
        if (props.onFocusedItemChange)
            props.onFocusedItemChange(focusedItem);
    }, [focusedItem]);

    useEffect(() => {
        if (props.focusedItem != focusedItem)
            setFocusedItem(props.focusedItem);
    }, [props.focusedItem]);

    useEffect(() => {
        if (props.onSortColumnsChanged)
            props.onSortColumnsChanged(sortColumns);
    }, [sortColumns]);

    useEffect(() => {
        props.onFilterChanged?.(filter);
    }, [filter]);

    useEffect(() => {
        props.onSelectionChanged?.({
            mode: selectedAll ? 'all' : 'ids',
            selectedIds: Array.from(selectedRows).map((x) => x.toString())
        });
    }, [selectedAll, selectedRows]);


    function setFilterValue(key: string, value: any | undefined): void {
        const newFilter = new Map<string, any>();
        filter.forEach((fv, fk) => {
            if (fk != key) {
                newFilter.set(fk, fv);
            }
        })
        if (!newFilter.has(key) && value) {
            newFilter.set(key, value);
        }
        setFilter(newFilter);

    }

    function buildFilterEditor(props: HeaderRendererProps<any, any>, column: ColumnDefinition<TItem>) {
        if (column.filterableOptions?.dropdownItems)
            return <Form.Dropdown options={column.filterableOptions?.dropdownItems}
                                  selection
                                  clearable
                                  value={filter?.get(column.key)}
                                  style={{fontSize: 13, fontWeight: 'normal'}}
                                  onChange={(e, data) => setFilterValue(column.key, data.value)}
                                  fluid/>
        else
            return <Form.Input placeholder={props.column.name} fluid size={"small"} content={filter?.get(column.key)}
                               onChange={(e: ChangeEvent<HTMLInputElement>) => setFilterValue(column.key, e.target.value)}
            />;
    }

    function handleHeaderCellClick(column: ColumnDefinition<TItem>) {
        const exists = (sortColumns.find((x) => x.columnKey == column.key));
        let direction: SortDirection = "ASC";
        if (exists && exists.direction == "ASC")
            direction = "DESC";
        setSortColumns([{columnKey: column.key, direction: direction}]);
    }

    function populateCols(): Column<any, any>[] {
        let result: Column<any, any>[] = [];
        if (multiSelect)
            result.push(
                {
                    ...SelectColumn,
                    // Режим выбора всех записей пока отключен
                    // headerRenderer: (props) => {
                    //     return <div>
                    //         <Checkbox
                    //             checked={selectedAll}
                    //             onClick={(event, data) => {
                    //                 setSelectedAll(data.checked ?? false);
                    //                 setSelectedRows(new Set());
                    //             }}/>
                    //     </div>;
                    // }
                },
            )
        props.columns?.forEach((c) => {
            result.push(
                {
                    key: c.key,
                    name: c.name,
                    sortable: c.sortable ?? true,
                    resizable: c.resizable,
                    width: c.width,
                    cellClass: (row) => {
                        if (c.cellClass)
                            return c.cellClass((row as any).item);
                        else
                            return undefined;
                    },
                    headerRenderer: isGridFilterable() ? (props) => {
                        return <div style={{padding: 0, overflow: "visible", contain: "style size"}}>
                            <div onClick={() => handleHeaderCellClick(c)}>
                                {props.sortDirection == "ASC" &&
                                    <p style={{position: "absolute", top: "6px", right: "8px"}}><Icon
                                        name={"sort down"}/>
                                    </p>}
                                {props.sortDirection == "DESC" &&
                                    <p style={{position: "absolute", top: "10px", right: "8px"}}><Icon
                                        name={"sort up"}/>
                                    </p>}
                                <p style={{marginTop: "8px", marginBottom: "6px"}}>{props.column.name}</p>
                            </div>
                            <div style={{
                                position: "relative",
                                zIndex: 999
                            }}>{c.filterable && buildFilterEditor(props, c)}</div>
                        </div>
                    } : undefined
                },
            );
        });
        return result;
    }

    function format(v: any, format: ColumnFormat): any {
        switch (format) {
            case ColumnFormat.date:
                return Utils.formatDate(v);
            case ColumnFormat.datetime:
                return Utils.formatDateTime(v);
            default:
                return v?.toString();
        }

    }

    function populateRows() {
        return (props.items ?? []).map((x, index) => {
            const row: any = {};
            row[props.keyProperty] = (x as any)[props.keyProperty];
            props.columns?.forEach((c) => {
                let v: any;
                if (c.accessor)
                    v = c.accessor(x);
                else
                    v = (x as any)[c.key];
                v = format(v, c.format ?? ColumnFormat.none);
                if (v)
                    row[c.key] = v;
            });
            row.item = x;
            return row;
        });
    }

    const cols = populateCols();
    const rows = populateRows();

    function onRowClick(prop: CellClickArgs<any>) {
        const item = prop.row.item;
        setFocusedItem(item);
    }

    function rowClass(row: any) {
        if (focusedItem == row.item)
            return styles.trCellSelected;
    }

    function onRowDoubleClick(prop: CellClickArgs<any>) {
        if (props.onItemDoubleClick)
            props.onItemDoubleClick(prop.row.item);
    }

    function isGridFilterable() {
        return (props.columns && (props.columns!.findIndex((col) => col.filterable == true) >= 0)) ?? false;
    }

    return <div>
        <DataGrid<TItem, string>
            style={{height: `${props.height ?? "450px"}`}}
            columns={cols}
            rows={rows}
            className={"rdg-light"}
            onCellClick={onRowClick}
            onCellDoubleClick={onRowDoubleClick}
            rowKeyGetter={(row) => (row as any)[props.keyProperty]}
            sortColumns={sortColumns}
            headerRowHeight={isGridFilterable() ? 76 : undefined}
            onCopy={(e: CopyEvent<any>) => window.navigator.clipboard.writeText(e.sourceRow[e.sourceColumnKey])}
            onSortColumnsChange={setSortColumns}
            rowClass={rowClass}
            selectedRows={!selectedAll ? selectedRows : new Set(rows.map((row) => (row as any)[props.keyProperty]))}
            onSelectedRowsChange={setSelectedRows}

        />

    </div>;
}

export default AppListDataGrid;